import React, { useState } from "react";
import moment from "moment";
import {
  campaignFormatHelper,
  getCampaignStatusText,
  getStatusCode,
} from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import LearnMoreButtonBase from "@/components/muiCustom/buttons/LearnMoreButton";
import DirectMailLocationListModal from "./DirectMailLocationListModal";
import { DIRECT_MAIL_CAMP_PROVIDER } from "@/features/campaigns/direct-mail/utils/directMailConstants";

type DirectMailLocation = {
  id?: string | number;
  [key: string]: any;
};

type JoinedPostcardCampaign = {
  id?: string | number;
  budget?: number;
  brand_contribution?: boolean;
  total_of_budget_and_brand_match?: number;
  participant_campaign?: {
    is_draft?: boolean;
    locationData?: DirectMailLocation[];
  };
  getCampaignProvider?: string;
  locations?: DirectMailLocation[];
  report_status?: string | number;
  status?: string | number;
  schedule_date?: string;
  mailing_date_range?: string;
  [key: string]: any;
};

type JoinedPostcardCampaignCardProps = {
  campaign?: JoinedPostcardCampaign;
};

const LearnMoreButton = LearnMoreButtonBase as React.ComponentType<any>;

function JoinedPostcardCampaignCard({
  campaign,
}: JoinedPostcardCampaignCardProps) {
  const {
    id,
    budget,
    brand_contribution,
    total_of_budget_and_brand_match,
    participant_campaign,
    getCampaignProvider,
    locations = [],
    report_status,
    status,
  } = campaign || {};

  const [showLocationModal, setShowLocationModal] = useState(false);

  const renderBudget = () => {
    let campaignBudget = budget || 0;
    if (brand_contribution) {
      campaignBudget = total_of_budget_and_brand_match || 0;
    }
    return <CurrencyFormateText value={campaignBudget} />;
  };

  const getScheduleDates = (campaign?: JoinedPostcardCampaign) => {
    const start = moment(campaign?.schedule_date);
    const end = start.isValid() ? moment(start).add(4, "day") : null;
    if (campaign?.schedule_date && start.isValid()) {
      return `${start.format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
    } else if (campaign?.mailing_date_range) {
      const [startDate, endDate] = campaign?.mailing_date_range.split(" - ");
      const formattedRange = `${moment(startDate).format("MM/DD/YYYY")} - ${moment(endDate).format("MM/DD/YYYY")}`;
      return formattedRange;
    }
    return "N/A";
  };

  const getLocationsCount = (): any => {
    if (
      participant_campaign &&
      participant_campaign.is_draft === false &&
      getCampaignProvider !== DIRECT_MAIL_CAMP_PROVIDER.RRD
    ) {
      const locs = participant_campaign.locationData || [];
      return locs;
    } else if (
      getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.RRD &&
      locations.length > 0
    ) {
      const locs = locations || [];
      return locs;
    }
    return 0;
  };

  const campaignStatus =
    (getCampaignProvider === DIRECT_MAIL_CAMP_PROVIDER.RRD &&
      report_status === "Delivered") ||
    report_status === 1 ||
    status === 1
      ? 6
      : 2;

  return (
    <div className="card h-100">
      <div className="card-body p-3">
        <div className="row">
          <div className="col-7 col-lg-3 fw-sb">
            {campaignFormatHelper(campaign).name(
              `/dashboard/direct-mail-report`
            )}
          </div>
          <div
            className="col-5 col-lg-2 text-right text-lg-left pl-lg-0 cursor-p"
            onClick={() => setShowLocationModal(true)}
          >
            <StatusChip
              label={getCampaignStatusText(campaignStatus)}
              status={getStatusCode(campaignStatus)}
              showOnlyLabel
            />
          </div>
          <div className="col-6 col-lg-2 mt-2 mt-lg-0 order-1 order-lg-0">
            <div className="d-lg-none title">Active Budget: </div>
            <span
              className="title-val fw-b cursor-p"
              onClick={() => setShowLocationModal(true)}
            >
              {renderBudget()}
            </span>
          </div>

          <div className="col-6 col-lg-2 mt-2 mt-lg-0">
            <div className="d-lg-none title">Enrolled Locations: </div>
            <span
              className="title-val fw-b cursor-p"
              style={{ color: "var(--primary-base)" }}
              onClick={() => setShowLocationModal(true)}
            >
              {getLocationsCount()?.length
                ? getLocationsCount()?.length
                : "N/A"}
            </span>
          </div>

          <div className="col-12 col-lg-2 mt-2 mt-lg-0 order-0 order-lg-1">
            <div className="d-lg-none title">Mailing Date: </div>
            <span
              className="title-val cursor-p"
              onClick={() => setShowLocationModal(true)}
            >
              {getScheduleDates(campaign) ? getScheduleDates(campaign) : "N/A"}
            </span>
          </div>

          <div className="col-12 col-lg-1 mt-lg-0 order-1">
            <div className="d-lg-none invisible title mb-1">
              Helpful Content :{" "}
            </div>
            <div className="d-flex align-items-center justify-content-end justify-content-lg-start">
              <LearnMoreButton
                to={{
                  pathname: `/dashboard/learn-more/direct-mail-campaign/${id}`,
                  state: { campaignId: id, from: "/dashboard" },
                }}
                icon={true}
                imagePath="/img/icons/bulb_pink.svg"
              />
            </div>
          </div>
        </div>
      </div>

      {showLocationModal && getLocationsCount()?.length > 0 && (
        <DirectMailLocationListModal
          locations={getLocationsCount()}
          showLocationModal={showLocationModal}
          setShowLocationModal={setShowLocationModal}
          getCampaignProvider={getCampaignProvider}
        />
      )}
    </div>
  );
}

export default JoinedPostcardCampaignCard;
